import EventEmitter from "events"
import {IPFS} from "ipfs-core"
import React, {useCallback, useContext, useEffect, useRef, useState} from "react"
import {BackendContext} from "../../../context/BackendContext"
import {UIHelperContext} from "../../../context/UIHelper.context"
import {notifyUpdate} from "../../../functions/ipfs-orbit"
import {ProfileDB} from "../../../models/ProfileDB.model"
import {Collaborator, RepoDB} from "../../../models/RepoDB.model"
import styles from "./index.module.scss"

interface MemberAccessProps {
  collaborators: Collaborator[]
  repoDB?: RepoDB
}

export const MemberAccess = (props: MemberAccessProps) => {
  const {collaborators, repoDB} = props
  const {setSnackMessage} = useContext(UIHelperContext)
  const {orbit, ipfs} = useContext(BackendContext)
  const [onAddNewMember, setOnAddNewMember] = useState(false)
  const userIDInput = useRef<HTMLInputElement>(null)
  const [collaboratorDB, setCollaboratorDB] = useState<ProfileDB>()
  const collaboratorDBReplEvent = useRef<EventEmitter>()
  const [collaboratorsList, setCollaboratorsList] = useState<Collaborator[]>(collaborators)

  const onClickAddNewMember = useCallback(() => {
    setOnAddNewMember(true)
  }, [])

  const onClickCancel = useCallback(() => {
    setOnAddNewMember(false)
  }, [])

  const updateCollaborator = useCallback((userID: string, name: string) => {
    setCollaboratorsList((prev) => {
      const newCollaborators = [...prev]

      let isUpdated = false

      for (let i = 0; i < prev.length; i++) {
        const collaborator = prev[i]
        if (collaborator.userID === userID) {
          newCollaborators[i] = {
            userID,
            name,
          }
          isUpdated = true
          break
        }
      }

      if (!isUpdated) {
        newCollaborators.push({
          userID,
          name,
        })
      }

      return newCollaborators
    })
  }, [])

  const onConfirmAddNewMember = useCallback(async () => {
    if (!orbit) {
      return
    }

    // Get user ID input value
    const userID = userIDInput.current?.value
    if (!userID) {
      return
    }

    // Validation
    const splits = userID.split("-")
    if (splits.length !== 4) {
      setSnackMessage("Invalid user ID")
      return
    }

    // Parse user ID to get the profile orbit address
    const userOrbitID = splits[2]
    const userPubKey = splits[3]
    const userOrbitAddress = `/orbitdb/${userOrbitID}/${userPubKey}`

    if (collaboratorDB !== undefined) {
      await collaboratorDB.close()
    }

    const profile = await orbit.keyvalue(userOrbitAddress) as ProfileDB
    setCollaboratorDB(profile)
    await profile.load()

    const name = profile.get("name")
    if (name) {
      updateCollaborator(userID, name)
      return
    }

    collaboratorDBReplEvent.current = profile.events.on("replicated", () => {
        const name = profile.get("name")
        if (name) {
          updateCollaborator(userID, name)
        }
      }
    )
  }, [orbit, collaboratorDB, updateCollaborator])

  const updateCollaboratorsInRepoDB = useCallback(async (
    ipfs: IPFS,
    repoDB: RepoDB,
    collaboratorsList: Collaborator[]
  ) => {
    const existingCollaboratorsId = repoDB
      .get("collaborators")?.map((c: Collaborator) => c.userID) || []

    const newCollaborators = collaboratorsList
      .filter((c) => !existingCollaboratorsId.includes(c.userID))

    if (newCollaborators.length === 0) {
      setOnAddNewMember(false)
      return
    }

    for (const c of newCollaborators) {
      const webIdentity = c.userID.split("-")[0]
      const desktopIdentity = c.userID.split("-")[1]
      await repoDB.access.grant("write", webIdentity)
      await repoDB.access.grant("write", desktopIdentity)
    }

    await repoDB.set("collaborators", collaboratorsList)
    await notifyUpdate(ipfs, repoDB.address.toString())
    setOnAddNewMember(false)
  }, [repoDB, collaborators])

  useEffect(() => {
    if (!ipfs || !collaboratorsList || !repoDB) {
      return
    }
    updateCollaboratorsInRepoDB(ipfs, repoDB, collaboratorsList).then()
  }, [ipfs, collaboratorsList, repoDB, updateCollaboratorsInRepoDB])

  useEffect(() => {
    return () => {
      collaboratorDBReplEvent.current?.removeAllListeners()
    }
  }, [])

  return (
    <div className={styles.Section}>
      <div className={styles.SubHeader}>
        Team ({collaboratorsList.length})
      </div>
      <div className={styles.SectionRow}>
        <table className={styles.AccessTable}>
          <thead className={styles.TableHeader}>
          <tr className="border border-lightgray rounded">
            <th className={styles.TableCell}>Name</th>
            <th className={styles.TableCell}>Wallet Address</th>
          </tr>
          </thead>
          <tbody>

          {
            collaboratorsList.map((collaborator) => (
              <tr className={styles.TableRow} key={collaborator.userID}>
                <td className={styles.TableCell}>
                  {collaborator.name}
                </td>
                <td className={styles.TableCell}>
                  {collaborator.userID?.split("-")[3]}
                </td>
              </tr>
            ))
          }

          </tbody>
        </table>

        {
          onAddNewMember
            ? (
              <div className={styles.NewMemberInputRow}>
                <input className={styles.MemberInput}
                       placeholder="Enter User ID"
                       ref={userIDInput}
                />
                <button className={styles.ConfirmAddBtn}
                        onClick={onConfirmAddNewMember}
                >
                  Add
                </button>
                <button className={styles.ConfirmAddBtn}
                        onClick={onClickCancel}
                >
                  Cancel
                </button>
              </div>
            )
            : (
              <button className={styles.AddBtn}
                      onClick={onClickAddNewMember}>
                + New Member
              </button>
            )
        }

      </div>
    </div>
  )
}
